<template>
  <UIModal
    class="ModalPublishToWeb"
    size="lg"
    :isOpen="isOpen"
    :isDismissable="showCloseButton"
    @close="onRequestClose"
  >
    <template #title>
      <div class="ModalPublishToWeb__title">
        <h2 v-if="step === 'form'">{{ formTitle }}</h2>
        <h2 v-else>{{ successTitle }}</h2>
      </div>
    </template>

    <template #content-container>
      <PublishToWebForm
        v-if="step === 'form'"
        :isOpen="isOpen"
        :isPublishing="isPublishing"
        :isUpdate="isUpdate"
        :hadPassword="hadPassword"
        :willReplacePublished="willReplacePublished"
        :usersWithAccess="usersWithAccess"
        :initialValues="formInitialValues"
        :websiteId="effectiveWebsiteId"
        :ownedSlug="ownedSlug"
        @submit="onPublishSubmit"
        @close="emit('close')"
      />
      <PublishToWebSuccess
        v-else
        :publishedShareHref="publishedShareHref"
        @close="emit('close')"
        @openLivePreview="openPublishedSharePage"
      />
    </template>
  </UIModal>
</template>

<script lang="ts" setup>
import { computed, inject, ref, watch } from 'vue'

import { UIModal } from '@foundever/ui-lib'

import { WebsitesApi } from '@/apiClient/modules/websites'
import type { ISelectedUser } from '@/apiClient/types/user'
import type { IWebsitePermission } from '@/apiClient/types/websites'
import { useHtmlPreviewPublish } from '@/composables/useHtmlPreviewPublish'
import { usePreviewStore } from '@/store/preview'
import { useToast } from '@/store/toast'
import {
  extractHtmlPageTitle,
  slugifyWebsiteName,
  websitePublicHref,
} from '@/utils/websiteSlug'
import { websitePublishKey } from '@/views/components/preview/websitePublishKey'

import PublishToWebForm from './components/PublishToWebForm.vue'
import PublishToWebSuccess from './components/PublishToWebSuccess.vue'
import {
  emptyPublishToWebFormValues,
  type PublishToWebFormValues,
} from './types'
import {
  buildWebsiteVisibilityBody,
  resolveWebsiteVisibilityMode,
} from './websiteVisibility'

type PublishStep = 'form' | 'success'

const previewStore = usePreviewStore()
const htmlPreview = useHtmlPreviewPublish()
const websitePublish = inject(websitePublishKey)
if (!websitePublish) {
  throw new Error('websitePublishKey is not provided')
}
const { publishWebsite, isPublishing } = websitePublish

const emit = defineEmits<{ close: [] }>()
const props = defineProps<{
  isOpen: boolean
  websiteId?: string | null
}>()

const step = ref<PublishStep>('form')
const publishedShareHref = ref('')
const hadPassword = ref(false)
const usersWithAccess = ref<ISelectedUser[]>([])
const formInitialValues = ref<PublishToWebFormValues>(
  emptyPublishToWebFormValues(),
)
/** Slug already owned by the resolved target website — lets the slug check
 * treat re-using it as available instead of "taken". */
const ownedSlug = ref<string | null>(null)
/**
 * Whether the resolved target website is currently published. Drives the modal title:
 * a published target reads as "Edit settings", an unpublished/new one as a fresh
 * "Publish this webpage?". Distinct from {@link isUpdate}, which is also true for an
 * unpublished library entry (that flow still publishes, not edits settings).
 */
const targetIsPublished = ref(false)

const isLibraryEntry = computed(() => Boolean(props.websiteId))

const effectiveWebsiteId = computed(
  () => props.websiteId ?? htmlPreview.effectiveWebsiteId.value ?? null,
)

const isUpdate = computed(() => {
  if (isLibraryEntry.value) return true
  return (
    htmlPreview.isWebsitePublished.value ||
    Boolean(htmlPreview.effectiveWebsiteId.value)
  )
})

/**
 * The chat already has a published webpage and the block being published isn't it,
 * so publishing replaces the currently live one (one webpage per conversation).
 * Never for a library entry — that flow edits a standalone website, not the chat's.
 */
const willReplacePublished = computed(
  () =>
    !isLibraryEntry.value &&
    Boolean(htmlPreview.chatWebsite.value?.isPublished) &&
    !htmlPreview.selectedBlockIsLive.value,
)

const showCloseButton = computed(() => !isPublishing.value)

const formTitle = computed(() =>
  targetIsPublished.value ? 'Edit settings' : 'Publish this webpage?',
)
const successTitle = computed(() =>
  targetIsPublished.value ? 'Asset updated' : 'Asset published',
)

function permissionsToSelectedUsers(
  permissions: IWebsitePermission[],
): ISelectedUser[] {
  return permissions.map((permission) => ({
    id: permission.user_id,
    employee_id: permission.employee_id ?? undefined,
    name: permission.user_name ?? '',
    email: permission.user_email ?? '',
  }))
}

function buildInitialFormValues(
  websiteDetail?: Awaited<ReturnType<typeof WebsitesApi.get>>,
  grantees: ISelectedUser[] = [],
): PublishToWebFormValues {
  // For a fresh chat block (no linked website), the store's name/slug are seeded by
  // the toolbar's watcher — a sibling component. When re-opening with the preview
  // panel already mounted, that seed can lag this modal's populate pass, leaving the
  // fields blank. Derive the same default straight from the previewed HTML as a
  // fallback so the prefill is deterministic regardless of cross-component ordering.
  const derivedTitle = extractHtmlPageTitle(htmlPreview.htmlContent.value)
  return {
    websiteName:
      websiteDetail?.name ?? (htmlPreview.websiteName.value || derivedTitle),
    websiteDescription:
      websiteDetail?.description ?? htmlPreview.websiteDescription.value ?? '',
    websiteSlug:
      websiteDetail?.slug ??
      (htmlPreview.websiteSlug.value || slugifyWebsiteName(derivedTitle)),
    websitePassword: '',
    visibilityMode: websiteDetail
      ? resolveWebsiteVisibilityMode({
          is_public: websiteDetail.is_public,
          has_password: websiteDetail.has_password,
        })
      : 'foundever',
    shareUsers: grantees,
  }
}

/** Fetch a website and prefill the form from it. Same for both entry points — a
 *  library row and the chat preview's resolved website — only the failure
 *  handling differs (see the watcher). */
async function loadWebsiteIntoForm(websiteId: string) {
  const website = await WebsitesApi.get(websiteId)
  targetIsPublished.value = website.is_published
  hadPassword.value = website.has_password
  const grantees = permissionsToSelectedUsers(website.permissions)
  usersWithAccess.value = []
  formInitialValues.value = buildInitialFormValues(website, grantees)
  ownedSlug.value = website.slug
}

// `immediate` so the form is built even when the modal is *born* open. The HTML
// card's "Publish to Web" action opens the preview panel and raises the publish
// request in one go, so `PreviewPanel` calls `publishModal.open()` during its own
// setup — before this modal mounts. Without `immediate`, `props.isOpen` is already
// `true` at mount and never transitions, so the watcher never fires and the name/slug
// prefill is skipped. The toolbar seeds those store values first (it mounts ahead of
// this modal), so the immediate run reads them. Opening from an already-mounted modal
// (preview toolbar, library card) still fires on the `false → true` transition.
watch(
  () => props.isOpen,
  async (open) => {
    if (!open) return

    step.value = 'form'
    publishedShareHref.value = ''
    hadPassword.value = false
    usersWithAccess.value = []
    ownedSlug.value = null
    // Seed from the previewed block's known publish state so the title doesn't flash
    // "Publish this webpage?" before the website fetch resolves; the library/preview
    // fetches below overwrite it with the authoritative `is_published`.
    targetIsPublished.value = htmlPreview.isWebsitePublished.value

    // A library entry has nothing to fall back to — the modal exists to edit that
    // one website — so a failed load closes it.
    if (props.websiteId) {
      try {
        await loadWebsiteIntoForm(props.websiteId)
      } catch {
        useToast().onError('Could not load website details.')
        emit('close')
      }
      return
    }

    // Chat preview: a failed load still leaves a usable "publish this block" form,
    // so fall through to the derived defaults below.
    const websiteId = htmlPreview.effectiveWebsiteId.value
    if (websiteId) {
      try {
        await loadWebsiteIntoForm(websiteId)
        return
      } catch {
        useToast().onError('Could not load website details.')
      }
    }

    formInitialValues.value = buildInitialFormValues()
  },
  { immediate: true },
)

function onRequestClose() {
  if (isPublishing.value) return
  emit('close')
}

function openPublishedSharePage() {
  const href = publishedShareHref.value
  if (!href) return
  window.open(href, '_blank', 'noopener,noreferrer')
}

async function onPublishSubmit(values: PublishToWebFormValues) {
  // A library entry already stores its HTML server-side; editing its settings must
  // not re-upload (and overwrite) its page, so send no `htmlContent` and let the
  // update omit `file`. Only the chat-preview publish flow supplies fresh inline
  // HTML from the previewed block.
  const htmlContent = isLibraryEntry.value
    ? undefined
    : htmlPreview.htmlContent.value

  if (!isLibraryEntry.value && !htmlContent?.trim()) {
    useToast().onError('No HTML content to publish.')
    return
  }

  const visibilityBody = buildWebsiteVisibilityBody(
    values.visibilityMode,
    values.websitePassword,
    values.shareUsers,
    { hadPassword: hadPassword.value, usersWithAccess: usersWithAccess.value },
  )

  const content = previewStore.content
  const htmlPublishKey = isLibraryEntry.value
    ? undefined
    : content?.type === 'html'
      ? content.htmlPublishKey
      : undefined

  try {
    const { website } = await publishWebsite({
      htmlContent,
      slug: values.websiteSlug,
      name: values.websiteName,
      description: null,
      is_public: visibilityBody.is_public ?? false,
      ...(visibilityBody.password ? { password: visibilityBody.password } : {}),
      ...(visibilityBody.remove_password
        ? { remove_password: visibilityBody.remove_password }
        : {}),
      ...(visibilityBody.permissions !== undefined
        ? { permissions: visibilityBody.permissions }
        : {}),
      websiteId: effectiveWebsiteId.value,
      publishPath: isLibraryEntry.value ? null : htmlPreview.publishPath.value,
      htmlPublishKey,
    })
    publishedShareHref.value = websitePublicHref(website.link)
    step.value = 'success'
  } catch {
    /* toast handled in useWebsitePublish */
  }
}
</script>

<style lang="scss">
.ModalPublishToWeb.UIModalContainer {
  --modal-size: 29.5rem;

  :deep(.UIModalContent) {
    height: auto;
    flex: 0 1 auto;
    overflow: visible;
  }

  .ModalPublishToWeb__title {
    display: flex;
    align-items: center;
    gap: var(---fds-global-space-sm);

    h2 {
      margin: 0;
      @include font-heading3;
      color: var(--text-primary);
    }
  }
}
</style>
